import { useEffect, useState, forwardRef, useCallback } from "react";
import Chart from "react-apexcharts";
import Icons from "../../icons";
import {
  backgroundAnotation,
  chartSeries,
  chartOptions,
} from "../../../../pages/reports/Reports/deviceGraph/DeviceGraphOptions";
import "./deviceChart.scss";
import { getModeValue } from "../../../constants/deviceConstants";

const DeviceChart = ({ graphData, telemetryDate }, ref) => {
  const [isHumidityChecked, setIsHumidityChecked] = useState(true);
  const [isCO2Checked, setIsCO2Checked] = useState(true);
  const [isShotSizeChecked, setIsShotSizeChecked] = useState(true);
  const [seriesData, setSeriesData] = useState([]);
  const [optionData, setOptionData] = useState({});
  const [showData, setShowData] = useState(false);
  const [chartKey, setChartKey] = useState(false);

  const handleToggleHumidity = useCallback((e) => {
    setIsHumidityChecked(e.target.checked);
  }, []);

  const handleToggleCio2 = useCallback((e) => {
    setIsCO2Checked(e.target.checked);
  }, []);

  const handleToggleShot = useCallback((e) => {
    setIsShotSizeChecked(e.target.checked);
  }, []);

  /**
   * This is a function that calclulates the time duration and displays date and time in Desired Custom Date Format.
   * Less than 1 day - HH:MM AM/PM.
   * Multiple Years - YYYY/MM/DD HH:MM AM/PM.
   * @param {array} timestamp - the Selected Time Duration.
   * @returns {number} Desired Custom Date-Time Format.
   */

  const timeFormatter = useCallback(
    (value, timestamp) => {
      const msInDay = 24 * 60 * 60 * 1000;
      const msInWeek = 7 * msInDay;
      const date = new Date(timestamp);
      const startDate = new Date(telemetryDate[0]);
      const endDate = new Date(telemetryDate[1]);

      const durationDiff = endDate.getTime() - startDate.getTime();

      let isMultipleYear = false;

      if (endDate.getFullYear() !== startDate.getFullYear()) {
        // Across multiple years
        isMultipleYear = true;
      }

      let optionsDate;

      if (startDate.getDate() === endDate.getDate()) {
        // Less than or equal to 1 day and Should Not include Previous Day
        const optionsTime = {
          hour: "2-digit",
          minute: "2-digit",
        };

        optionsDate = {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        };

        const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

        return [formattedTime];
      } else if (durationDiff <= msInWeek) {
        // Includes Previous Day

        if (isMultipleYear) {
          optionsDate = {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          };
        } else {
          optionsDate = {
            month: "2-digit",
            day: "2-digit",
          };
        }

        const optionsTime = {
          hour: "2-digit",
          minute: "2-digit",
        };

        const formattedDate = date.toLocaleDateString("en-ZA", optionsDate);
        const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

        return [formattedDate, formattedTime];
      }
    },
    [telemetryDate]
  );

  const getOfflineModeGraph = useCallback(() => {
    const series = [...chartSeries];
    const options = { ...chartOptions };

    series[0].data = [];
    series[1].data = [];
    series[2].data = [];
    series[3].data = [
      [telemetryDate[0], null],
      [telemetryDate[1], null],
    ];

    options.xaxis.labels.formatter = timeFormatter;
    options.yaxis[0].title = "";
    options.yaxis[1].title = "";
    options.yaxis[2].title = "";
    options.annotations.xaxis = [
      {
        ...backgroundAnotation["OFFLINE"],
        x: new Date(telemetryDate[0]).getTime(),
        x2: new Date(telemetryDate[1]).getTime(),
      },
    ];
    setSeriesData(series);
    setOptionData(options);
    setShowData(true);
    setChartKey((prev) => !prev);
  }, [telemetryDate, timeFormatter]);

  const getSeriesData = useCallback(
    (isHumidityChecked, isCO2Checked, isShotSizeChecked) => {
      const series = [...chartSeries];
      const options = { ...chartOptions };

      const modeChangeList = graphData?.modeEvent.map((modesData) => {
        return {
          ...backgroundAnotation[getModeValue(modesData.modes)],
          x: new Date(modesData.startTime).getTime(),
          x2: new Date(modesData.endTime).getTime(),
        };
      });

      series[0].data =
        isCO2Checked && graphData?.telemetryData.clo2.length
          ? graphData?.telemetryData.clo2
          : [];
      series[1].data =
        isHumidityChecked && graphData?.telemetryData.humidity.length
          ? graphData?.telemetryData.humidity
          : [];
      series[2].data =
        isShotSizeChecked && graphData?.telemetryData.lastShotSize.length
          ? graphData?.telemetryData.lastShotSize
          : [];

      series[3].data = [
        [new Date(telemetryDate[0]), null],
        [new Date(telemetryDate[1]), null],
      ];

      if (
        !series[0].data.length &&
        !series[1].data.length &&
        !series[2].data.length
      ) {
        options.tooltip.enabled = false;
      }else {
        options.tooltip.enabled = true;
      }

      options.xaxis.labels.formatter = timeFormatter;
      options.annotations.xaxis = modeChangeList;
      options.yaxis[0].title = "";
      options.yaxis[1].title = "";
      options.yaxis[2].title = "";
      setSeriesData(series);
      setOptionData(options);
      setShowData(true);
      setChartKey((prev) => !prev);
    },
    [
      graphData?.modeEvent,
      graphData?.telemetryData,
      telemetryDate,
      timeFormatter,
    ]
  );

  useEffect(() => {
    if (graphData?.telemetryData) {
      getSeriesData(isHumidityChecked, isCO2Checked, isShotSizeChecked);
    } else {
      getOfflineModeGraph();
    }
  }, [
    graphData,
    getSeriesData,
    isHumidityChecked,
    isCO2Checked,
    isShotSizeChecked,
    getOfflineModeGraph,
  ]);

  return (
    <div id="chart-wrap" ref={ref}>
      <div className="d-flex fluid align-items-center justify-content-between">
        <div className="deviceTitle heading">Last 60 Minutes</div>
        <div className="inputBoxContainer">
          <div className="checkBoxWrapper ppb">
            <input
              type="checkbox"
              checked={isCO2Checked}
              onChange={(e) => handleToggleCio2(e)}
              id="ppbCheckbox"
            />
            <label htmlFor="ppbCheckbox">
              {isCO2Checked
                ? Icons.graphFilterChecked
                : Icons.graphFilterUnChecked}
              <span className="clo2-filter">CIO2</span>
            </label>
          </div>
          <div className="checkBoxWrapper humidity">
            <input
              type="checkbox"
              checked={isHumidityChecked}
              onChange={(e) => handleToggleHumidity(e)}
              id="humidityCheckbox"
            />
            <label htmlFor="humidityCheckbox">
              {isHumidityChecked
                ? Icons.graphFilterChecked
                : Icons.graphFilterUnChecked}
              <span className="humidity-filter">Humidity</span>
            </label>
          </div>
          <div className="checkBoxWrapper shotsize">
            <input
              type="checkbox"
              checked={isShotSizeChecked}
              onChange={(e) => handleToggleShot(e)}
              id="shotsizeCheckbox"
            />
            <label htmlFor="shotsizeCheckbox">
              {isShotSizeChecked
                ? Icons.graphFilterChecked
                : Icons.graphFilterUnChecked}
              <span className="shotsize-filter">Shot Size</span>
            </label>
          </div>
        </div>
      </div>
      {showData && (
        <div id="chart" style={{ height: "700px" }}>
          <Chart
            options={optionData}
            type="line"
            series={seriesData}
            width="100%"
            height="700px"
            key={chartKey}
          />
        </div>
      )}
    </div>
  );
};

export default forwardRef(DeviceChart);
